import { useEffect, useMemo } from 'react'
import { formatDateRu, parseChangelog } from '../lib/changelog'
import { BlockView } from './changelog/BlockView'

interface Props {
  open: boolean
  onClose: () => void
}

const entries = parseChangelog(__CHANGELOG__)

export function WhatsNewModal({ open, onClose }: Props) {
  useEffect(() => {
    if (!open) return
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [open, onClose])

  const formatted = useMemo(
    () =>
      entries.map(e => ({
        ...e,
        display: formatDateRu(e.date),
      })),
    [],
  )

  if (!open) return null

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-2xl my-4 md:my-0 max-h-[calc(100vh-2rem)] flex flex-col"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="whatsnew-title"
      >
        <div className="px-6 pt-6 pb-3 flex items-start justify-between border-b border-gray-100 dark:border-gray-800 shrink-0">
          <div>
            <h2
              id="whatsnew-title"
              className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              What's new
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Последние обновления МММ — версия веб-клиента v{__APP_VERSION__}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-1 -mt-1 -mr-1"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto">
          {formatted.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Пока ничего нового.
            </p>
          ) : (
            <div>
              {formatted.map((entry, entryIdx) => (
                <section
                  key={entry.date}
                  className={
                    entryIdx === 0
                      ? 'pb-8'
                      : 'pt-8 pb-8 border-t border-gray-200 dark:border-gray-800'
                  }
                >
                  <div className="flex flex-wrap items-baseline gap-2 mb-5">
                    <span className="inline-flex items-center rounded-full bg-blue-50 dark:bg-blue-950/40 px-3 py-1 text-sm font-semibold text-blue-700 dark:text-blue-300 tabular-nums">
                      {entry.display}
                    </span>
                    <span className="text-[10px] font-mono text-gray-400 dark:text-gray-600 tabular-nums">
                      {entry.date}
                    </span>
                    {entry.versions?.map(v => (
                      <span
                        key={v.domain}
                        className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-mono text-gray-700 dark:text-gray-300 tabular-nums"
                      >
                        {v.domain} {v.version}
                      </span>
                    ))}
                  </div>
                  <div className="space-y-4 pl-1">
                    {entry.blocks.map((block, idx) => (
                      <BlockView key={idx} block={block} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

